/**
 * Edit Reorder Settings Dialog
 * Matches CapsInventory UI exactly
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Box, Typography, IconButton, Paper, alpha, useTheme, Grid
} from '@mui/material';
import {
  Close as CloseIcon, Cancel as CancelIcon, Warning as WarningIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { supabase } from '../../../../supabaseClient';
import { getInventoryConfig } from './inventoryConfig';

export default function EditReorderDialog({ open, onClose, item, config, inventoryType, onSuccess }) {
  const theme = useTheme();
  const [formData, setFormData] = useState({ reorder_point: '', reorder_quantity: '' });
  const [saving, setSaving] = useState(false);

  // Use passed config or get from inventoryType if not provided
  const finalConfig = config || (inventoryType ? getInventoryConfig(inventoryType) : null);

  useEffect(() => {
    if (item) {
      setFormData({
        reorder_point: item.reorder_point !== null && item.reorder_point !== undefined ? item.reorder_point.toString() : '',
        reorder_quantity: item.reorder_quantity !== null && item.reorder_quantity !== undefined ? item.reorder_quantity.toString() : ''
      });
    }
  }, [item]);

  // Early return check AFTER all hooks
  if (!finalConfig) {
    console.error('No config available for EditReorderDialog');
    return null;
  }

  const handleSave = async () => {
    if (!item) return;

    try {
      setSaving(true);

      const isChemical = finalConfig.hasUnitOfMeasure;
      const reorderPoint = formData.reorder_point && formData.reorder_point.trim() !== ''
        ? parseFloat(formData.reorder_point) : null;
      const reorderQty = formData.reorder_quantity && formData.reorder_quantity.trim() !== ''
        ? parseFloat(formData.reorder_quantity) : null;

      if (formData.reorder_point && formData.reorder_point.trim() !== '' && (isNaN(reorderPoint) || reorderPoint < 0)) {
        alert('Reorder point must be a valid non-negative number');
        return;
      }

      if (formData.reorder_quantity && formData.reorder_quantity.trim() !== '' && (isNaN(reorderQty) || reorderQty < 0)) {
        alert('Reorder quantity must be a valid non-negative number');
        return;
      }

      // Get the actual item ID - aggregated data uses item_id, lot items use cap_id/bottle_id/etc
      const actualItemId = item.item_id || item[finalConfig.itemIdField];

      if (!actualItemId) {
        throw new Error(`Could not determine item ID. Checked item_id and ${finalConfig.itemIdField}`);
      }

      const companyId = item.company_id || item.location?.company_id;
      const lastUpdatedField = finalConfig.lastUpdatedField || 'last_updated';

      // Build the base query filter — for products, include variant_id so reorder is variant-specific
      const variantId = finalConfig.supportsVariants ? (item.variant_id || null) : null;

      const buildQuery = (query) => {
        query = query
          .eq('company_id', companyId)
          .eq(finalConfig.itemIdField, actualItemId)
          .eq('location_id', item.location_id);
        if (finalConfig.supportsVariants) {
          query = variantId ? query.eq('variant_id', variantId) : query.is('variant_id', null);
        }
        return query;
      };

      const { data: existingRows, error: checkError } = await buildQuery(
        supabase.from(finalConfig.inventoryTable).select('id')
      );

      if (checkError) throw checkError;

      // Chemicals use different column names for reorder thresholds
      // Products don't have a reorder_quantity column — only reorder_point
      const reorderFields = isChemical
        ? { reorder_point_units: reorderPoint, reorder_quantity_units: reorderQty }
        : finalConfig.hasReorderQuantity !== false
          ? { reorder_point: reorderPoint, reorder_quantity: reorderQty }
          : { reorder_point: reorderPoint };

      if (existingRows && existingRows.length > 0) {
        // Update matching rows using the compound filter (variant-specific for products)
        const { error } = await buildQuery(
          supabase.from(finalConfig.inventoryTable).update({
            ...reorderFields,
            [lastUpdatedField]: new Date().toISOString()
          })
        );

        if (error) throw error;
      } else {
        // No existing rows — insert one (not applicable for products, their rows are RPC-managed)
        if (finalConfig.hasReorderQuantity === false) {
          throw new Error('No inventory record found for this product. Add inventory first before setting a reorder point.');
        }

        const qtyFields = isChemical
          ? { current_unit_quantity: 0, current_total_quantity: 0 }
          : { current_quantity: 0 };

        const { error } = await supabase
          .from(finalConfig.inventoryTable)
          .insert([{
            company_id: companyId,
            [finalConfig.itemIdField]: actualItemId,
            location_id: item.location_id,
            ...qtyFields,
            ...reorderFields,
            [lastUpdatedField]: new Date().toISOString()
          }]);

        if (error) throw error;
      }

      if (onSuccess) await onSuccess('Reorder settings updated successfully');
      onClose();
    } catch (error) {
      console.error('Error updating reorder settings:', error);
      alert('Failed to update reorder settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, white)`,
          backdropFilter: 'blur(20px)',
          boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`
        }
      }}>
      <DialogTitle sx={{
        px: 0, py: 0,
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
        color: theme.palette.text.primary,
        fontWeight: 500,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2 }}>
          <InventoryIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            Update Reorder Settings
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{
          mr: 2,
          color: theme.palette.text.secondary,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            transform: 'scale(1.05)'
          }
        }}>
          <CloseIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          {item && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5 }}>
                {item.item_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.location_name}
              </Typography>
            </Box>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={finalConfig.hasReorderQuantity !== false ? 6 : 12}>
              <Paper sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
                overflow: 'hidden',
                p: 4
              }}>
                <TextField fullWidth label="Reorder Point" type="number"
                  value={formData.reorder_point}
                  onChange={(e) => setFormData({ ...formData, reorder_point: e.target.value })}
                  placeholder="Enter reorder point or leave empty"
                  helperText="Quantity at which to trigger reorder alerts"
                  inputProps={{ min: 0 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main
                      }
                    },
                    '& .MuiInputLabel-root': { fontWeight: 500 }
                  }} />
              </Paper>
            </Grid>

            {finalConfig.hasReorderQuantity !== false && (
            <Grid item xs={12} md={6}>
              <Paper sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
                overflow: 'hidden',
                p: 4
              }}>
                <TextField fullWidth label="Reorder Quantity" type="number"
                  value={formData.reorder_quantity}
                  onChange={(e) => setFormData({ ...formData, reorder_quantity: e.target.value })}
                  placeholder="Enter reorder quantity or leave empty"
                  helperText="Suggested quantity to order when below reorder point"
                  inputProps={{ min: 0 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main
                      }
                    },
                    '& .MuiInputLabel-root': { fontWeight: 500 }
                  }} />
              </Paper>
            </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        p: 4,
        borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        background: alpha(theme.palette.grey[50], 0.5),
        backdropFilter: 'blur(10px)'
      }}>
        <Button onClick={onClose} startIcon={<CancelIcon />}
          sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: 500 }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={<WarningIcon />}
          sx={{
            borderRadius: 2, px: 3, py: 1, fontWeight: 500,
            bgcolor: 'primary.main',
            boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
            '&:hover': {
              bgcolor: 'primary.dark',
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
            }
          }}>
          {saving ? 'Updating...' : 'Update Reorder Settings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
